import { ReactNode } from 'react';
import type { BankingDetails } from '../../../../graphql/generated/graphql';
import { Icon } from '../../../../components/icons/Icon';
import { LabelValueField } from '../../../../components/LabelValueField';
import { ErrorEntry } from '../../../../components/errors';
import { useTranslation } from '../../../../providers/UIProvider';
import { cn, combineIssues, severityStyle } from '../../../../utils';
import type { ValidationIssue } from '../../../../types';
import type { FormatterProps } from '../types/editor-config';
import { FormatterError } from './FormatterError';
import { Divider } from '../../../../components';
import { formatAccountNumber } from '@insio/banking';

type FieldKey =
  'bankAccount' | 'bankName' | 'iban' | 'swift' | 'bankCode' | 'varSymb' | 'constSymb' | 'specSymb';

const KNOWN_FIELDS: ReadonlySet<FieldKey> = new Set([
  'bankAccount',
  'bankName',
  'iban',
  'swift',
  'bankCode',
  'varSymb',
  'constSymb',
  'specSymb'
]);

interface GroupedIssues {
  byField: Partial<Record<FieldKey, ValidationIssue[]>>;
  sectionLevel: ValidationIssue[];
}

function groupIssues(errors: ValidationIssue[] | undefined): GroupedIssues {
  const byField: Partial<Record<FieldKey, ValidationIssue[]>> = {};
  const sectionLevel: ValidationIssue[] = [];
  for (const issue of errors ?? []) {
    const rawPath = issue.path ?? '';
    if (rawPath === '' || rawPath === 'bankingDetails') {
      sectionLevel.push(issue);
      continue;
    }
    const localPath = rawPath.startsWith('bankingDetails.')
      ? rawPath.slice('bankingDetails.'.length)
      : rawPath;
    if (KNOWN_FIELDS.has(localPath as FieldKey)) {
      (byField[localPath as FieldKey] ??= []).push(issue);
    } else {
      sectionLevel.push(issue);
    }
  }
  return { byField, sectionLevel };
}

const isBankingDetails = (value: unknown): value is BankingDetails =>
  typeof value === 'object' &&
  value !== null &&
  ('bankAccount' in value ||
    'bankCode' in value ||
    'bankName' in value ||
    'iban' in value ||
    'swift' in value ||
    'varSymb' in value ||
    'constSymb' in value ||
    'specSymb' in value);

function FieldWithError({
  value,
  issues
}: {
  value: ReactNode;
  issues: ValidationIssue[] | undefined;
}): ReactNode {
  return (
    <span className='inline-flex flex-col gap-2xs items-start'>
      <span>{value}</span>
      <FormatterError issues={issues} />
    </span>
  );
}

/**
 * Rich read-only display of a `BankingDetails` value: account + bank, IBAN,
 * SWIFT + bank code, and (read-only) payment symbols. Mirrors the layout the
 * detail-page banking section used to render directly. Uses
 * `useTranslation('modules.modals.editBankingDetails')` so labels stay in sync
 * with the edit modal.
 */
export function BankingDetailsFormatter({ value, errors }: FormatterProps): ReactNode {
  const t = useTranslation('modules.modals.editBankingDetails');

  const data = isBankingDetails(value) ? value : null;
  const { byField, sectionLevel } = groupIssues(errors);
  const sectionCombined = combineIssues(sectionLevel);
  const sectionSeverity = severityStyle(sectionCombined?.severity);

  return (
    <div className={cn('w-full rounded-xl bg-white border-none ', sectionSeverity)}>
      <div className='p-md space-y-md'>
        {sectionCombined && sectionSeverity && (
          <div
            className={cn('flex items-start gap-xs rounded px-xs py-2xs', sectionSeverity.bgColor)}
          >
            <Icon
              name={sectionSeverity.icon}
              className={cn('h-3.5 w-3.5 shrink-0 mt-2xs', sectionSeverity.iconColor)}
              aria-hidden='true'
            />
            <span className={cn('text-xs', sectionSeverity.iconColor)}>
              <ErrorEntry issue={sectionCombined} showActual />
            </span>
          </div>
        )}

        <div className='md:flex gap-md space-y-md'>
          <LabelValueField
            label={t('fields.accountNumberWithCode')}
            value={
              <FieldWithError value={formatAccountNumber(data)} issues={byField.bankAccount} />
            }
            bold
          />
          <LabelValueField
            label={t('fields.bankName')}
            value={<FieldWithError value={data?.bankName} issues={byField.bankName} />}
          />
        </div>

        <div className='md:flex gap-md space-y-md'>
          <LabelValueField
            label={t('fields.iban')}
            value={<FieldWithError value={data?.iban} issues={byField.iban} />}
          />

          <LabelValueField
            label={t('fields.swift')}
            value={<FieldWithError value={data?.swift} issues={byField.swift} />}
          />
        </div>
        <div className='md:flex gap-md space-y-md'></div>

        <Divider variant='light' thickness='sm' className='my-md' />

        {/* Variable + Constant Symbols */}
        <div className='md:flex gap-md space-y-md'>
          <LabelValueField
            label={t('fields.varSymb')}
            value={<FieldWithError value={data?.varSymb} issues={byField.varSymb} />}
            bold
          />
          <LabelValueField
            label={t('fields.constSymb')}
            value={<FieldWithError value={data?.constSymb} issues={byField.constSymb} />}
            bold
          />
        </div>

        {/* Specific Symbol */}
        <LabelValueField
          label={t('fields.specSymb')}
          value={<FieldWithError value={data?.specSymb} issues={byField.specSymb} />}
          bold
        />
      </div>
    </div>
  );
}

BankingDetailsFormatter.displayName = 'BankingDetailsFormatter';
